import { useEffect } from 'react';
import { getPreferences, type Preferences } from '@/services/preferences';
import { storage } from '@/services/storage';

type Theme = NonNullable<Preferences['theme']>;

// applyTheme writes the <html> class that Tailwind's darkMode:'class'
// config reacts to. Exported so Settings can call it synchronously
// right after the user changes the dropdown — no wait-for-save, no
// page reload. Also caches the choice so the next boot is instant.
const THEME_CACHE = 'ira.theme';

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  root.classList.toggle('dark', isDark);
  localStorage.setItem(THEME_CACHE, theme);
}

// useTheme wires the preference → DOM class once per app boot:
//   1. Immediately apply a cached choice from localStorage (instant paint)
//   2. Listen for system-theme changes while 'system' is selected
//   3. Fetch server preferences and re-apply if the server overrides local
// Keeping this in one place means every mount path (taskpane OR admin)
// can call useTheme() and get identical behavior.
export function useTheme(fetchRemote = true) {
  useEffect(() => {
    // Step 1 — paint instantly from cache.
    const cached = (localStorage.getItem(THEME_CACHE) as Theme | null) ?? 'system';
    applyTheme(cached);

    // Step 2 — if cached is 'system', follow OS changes while tab is open.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystemChange = () => {
      const current = (localStorage.getItem(THEME_CACHE) as Theme | null) ?? 'system';
      if (current === 'system') applyTheme('system');
    };
    media.addEventListener('change', onSystemChange);

    // Step 3 — reconcile with server if authed. Silently skipped on the
    // login screen (no token). Explicitly gated so the admin portal's
    // pre-auth view doesn't make an unnecessary request.
    if (fetchRemote && storage.getAccess()) {
      getPreferences()
        .then((p) => {
          if (p.theme) applyTheme(p.theme);
        })
        .catch(() => { /* non-fatal — cached theme already painted */ });
    }

    return () => media.removeEventListener('change', onSystemChange);
  }, [fetchRemote]);
}
